"use client";

import { motion } from "framer-motion";
import Link from "next/link";
import { useIntlayer } from "next-intlayer";

const GLYPHS = [
  { symbol: "∑", x: "10%", y: "20%", size: "4rem", delay: 0.6 },
  { symbol: "∫", x: "80%", y: "15%", size: "5rem", delay: 0.8 },
  { symbol: "π", x: "70%", y: "65%", size: "3.5rem", delay: 1.0 },
  { symbol: "∂", x: "20%", y: "70%", size: "3rem", delay: 0.7 },
  { symbol: "√", x: "55%", y: "25%", size: "3rem", delay: 1.2 },
  { symbol: "∞", x: "35%", y: "80%", size: "3.5rem", delay: 0.9 },
  { symbol: "Δ", x: "88%", y: "45%", size: "4rem", delay: 1.1 },
];

export default function HeroSection() {
  const { tagline, headlineMain, headlineHighlight, body, browseCourses, seeHowItWorks } =
    useIntlayer("hero-section");

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 bg-background">
      {/* Graph paper grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage: `
            linear-gradient(#0193cd 1px, transparent 1px),
            linear-gradient(90deg, #0193cd 1px, transparent 1px)
          `,
          backgroundSize: "40px 40px",
        }}
      />

      {/* Floating math glyphs */}
      {GLYPHS.map((g) => (
        <motion.span
          key={g.symbol}
          className="absolute select-none pointer-events-none font-light text-[#0193cd]"
          style={{ left: g.x, top: g.y, fontSize: g.size }}
          initial={{ opacity: 0, y: 10 }}
          animate={{
            opacity: [0, 0.2, 0.2, 0],
            y: [10, -10, 10],
            rotate: [-5, 5, -5],
          }}
          transition={{
            delay: g.delay,
            duration: 6,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {g.symbol}
        </motion.span>
      ))}

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <motion.p
          className="text-xs font-semibold tracking-[0.3em] uppercase mb-6 text-[#0193cd]"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          {tagline}
        </motion.p>

        <motion.h1
          className="text-6xl md:text-7xl lg:text-8xl font-semibold leading-tight mb-6 text-foreground"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15, ease: "easeOut" }}
        >
          {headlineMain}
          <br />
          <span className="text-[#0193cd]">{headlineHighlight}</span>
        </motion.h1>

        <motion.p
          className="text-lg md:text-xl mb-10 max-w-2xl mx-auto text-muted-foreground"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
        >
          {body}
        </motion.p>

        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45, ease: "easeOut" }}
        >
          <Link
            href="/courses"
            className="px-8 py-3 rounded-full font-semibold text-white transition-all hover:opacity-90 hover:scale-105 bg-[#0193cd]"
          >
            {browseCourses}
          </Link>
          <Link
            href="#why"
            className="px-8 py-3 rounded-full font-semibold border transition-all hover:bg-[#0193cd] hover:text-white border-[#0193cd] text-[#0193cd]"
          >
            {seeHowItWorks}
          </Link>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
        aria-hidden="true"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#0193cd"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </motion.div>
    </section>
  );
}
